'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface Course {
  id: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  gradient: string;
}

export default function CoursesSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const courses: Course[] = [
    {
      id: 'course_ai',
      title: 'AI Complete Course',
      description: 'Master Python, Machine Learning, Deep Learning with real-world AI projects',
      icon: 'CpuChipIcon',
      color: 'course-ai',
      gradient: 'from-blue-500 to-blue-600',
    },
    {
      id: 'course_graphic',
      title: 'Graphic Designing',
      description: 'Learn Photoshop, Illustrator, Figma and build stunning design portfolios',
      icon: 'PaintBrushIcon',
      color: 'course-graphic',
      gradient: 'from-purple-500 to-purple-600',
    },
    {
      id: 'course_marketing',
      title: 'Digital Marketing',
      description: 'Master SEO, Social Media, Google Ads and analytics for business growth',
      icon: 'MegaphoneIcon',
      color: 'course-marketing',
      gradient: 'from-orange-500 to-orange-600',
    },
    {
      id: 'course_coding',
      title: 'Coding',
      description: 'Learn HTML, CSS, JavaScript, React with hands-on coding projects',
      icon: 'CodeBracketIcon',
      color: 'course-coding',
      gradient: 'from-green-500 to-green-600',
    },
    {
      id: 'course_video',
      title: 'Video Editing',
      description: 'Master Premiere Pro, After Effects, color grading and motion graphics',
      icon: 'FilmIcon',
      color: 'course-video',
      gradient: 'from-red-500 to-red-600',
    },
    {
      id: 'course_web',
      title: 'Website Development',
      description: 'Build websites with WordPress, Webflow and custom coding skills',
      icon: 'GlobeAltIcon',
      color: 'course-web',
      gradient: 'from-cyan-500 to-cyan-600',
    },
    {
      id: 'course_app',
      title: 'App Building',
      description: 'Create mobile apps with React Native, Flutter, UI/UX and deployment',
      icon: 'DevicePhoneMobileIcon',
      color: 'course-app',
      gradient: 'from-indigo-500 to-indigo-600',
    },
  ];

  return (
    <section ref={sectionRef} className="py-20 md:py-32 px-6 bg-secondary">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className={`text-center mb-16 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
          <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2 mb-6">
            <Icon name="AcademicCapIcon" size={16} className="text-primary" />
            <span className="text-xs font-semibold uppercase tracking-wider text-white">
              Our Courses
            </span>
          </span>
          <h2 className="font-display text-5xl md:text-7xl uppercase tracking-tighter leading-none text-white mb-4">
            Choose Your Path
          </h2>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            Industry-ready courses designed to transform your career with practical skills
          </p>
        </div>

        {/* Courses Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {courses.map((course, index) => (
            <div
              key={course.id}
              className={`group relative overflow-hidden bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300 hover:scale-105 hover:shadow-2xl ${
                isVisible ? 'animate-fadeIn' : 'opacity-0'
              }`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Gradient Overlay */}
              <div className={`absolute inset-0 bg-gradient-to-br ${course.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>

              {/* Icon */}
              <div className={`relative inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br ${course.gradient} mb-4`}>
                <Icon name={course.icon as any} size={28} className="text-white" />
              </div>

              {/* Content */}
              <h3 className="relative font-display text-xl uppercase tracking-tight text-white mb-2">
                {course.title}
              </h3>
              <p className="relative text-sm text-white/70 leading-relaxed mb-4">
                {course.description}
              </p>

              {/* Learn More Link */}
              <Link
                href="/courses"
                className="relative inline-flex items-center gap-2 text-sm font-semibold text-primary group-hover:text-white transition-colors"
              >
                Learn More
                <Icon name="ArrowRightIcon" size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>

        {/* View All Button */}
        <div className={`text-center mt-12 ${isVisible ? 'animate-fadeInUp delay-700' : 'opacity-0'}`}>
          <Link
            href="/courses"
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 rounded-full text-base font-semibold uppercase tracking-wider hover:bg-primary/90 transition-all shadow-xl hover:shadow-2xl hover:scale-105"
          >
            View All Courses
            <Icon name="ArrowRightIcon" size={20} />
          </Link>
        </div>
      </div>
    </section>
  );
}